<template>
 <div
  class="custom-node"
  :class="[
  data.cellClass,
    { 
      'node-3d': !data.invisibleConnector && !data.onlyImage && !data.templateCell,
      'no-border': data.noBorder,
      'compact-node': data.compact,
      'invisible-connector': data.invisibleConnector,
      'image-only-node': data.onlyImage,
      'template-cell': data.templateCell,
      'template-cell-empty': data.emptyCell,
      'cell-results': data.showResult && data.templateCell,
      'results-card-node': data.resultsCard,
      'arrow-node': data.arrowNode,
    }
  ]"
  :style="{ background: data.bgColor || 'transparent' }"
>
    <div class="node-image-wrapper" v-if="data.image && !data.emptyCell">
  <img 
    :src="data.image" 
    :alt="data.title || 'Nodo del diagrama'" 
    class="node-image"
  />
</div>

<div v-else-if="data.emptyCell" class="empty-cell-dot"></div>

    <div class="icon" v-else-if="data.icon" v-html="`<i class='mdi ${data.icon}'></i>`"></div>

    <div class="node-text" v-if="!data.emptyCell">
  <div v-if="data.title" class="node-title">
    {{ data.title }}
  </div>

  <div class="content" v-html="data.html"></div>
</div>

      

    <!-- Target handles -->
    <template v-if="!data.noHandles">
      <Handle
        v-for="position in targetPositions"
        :key="'target-' + position"
        type="target"
        :position="position"
        :id="position"
      />
    </template>

    <!-- Source handles -->
    <template v-if="!data.noHandles">
      <Handle
        v-for="position in sourcePositions"
        :key="'source-' + position"
        type="source"
        :position="position"
        :id="position"
      />
    </template>
  </div>
</template>

<script>
import { Handle, useVueFlow } from '@vue-flow/core'
import { computed } from 'vue'

export default {
  name: 'CustomNode',
  props: {
    id: {
      type: String,
      required: true
    },
    data: {
      type: Object,
      default: () => ({
        title: '',
        html: '',
        icon: '',
        image: '',
        bgColor: '',
        noBorder: false,
        noHandles: false,
        sourcePositions: ['bottom'],
        targetPositions: ['top']
      })
    }
  },
  components: { Handle },
  setup(props) {
    const { findNode } = useVueFlow()

    const node = computed(() => findNode(props.id) || { position: { x: 0, y: 0 } })
    const position = computed(() => node.value.position)

    return {
      position
    }
  },
  data() {
    return {
      defaultBackground: 'linear-gradient(145deg, #ffffff, #e8f6f6)'
    }
  },
  computed: {
    sourcePositions() {
      return this.data.sourcePositions || []
    },
    targetPositions() {
      return this.data.targetPositions || []
    }
  }
}
</script>

<style scoped>
.custom-node {
  position: relative;
  min-width: 120px;
  min-height: 80px;
  padding: 10px 12px;
  border-radius: 16px;
  border: 1px solid rgba(42, 140, 140, 0.35);
  color: #174f4f;
  text-align: center;
  overflow: visible;
  background: transparent;
}

/* Base imagen/texto */
.node-image-wrapper {
  width: 100%;
  height: 56px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 6px;
}

.node-image {
  max-width: 70px;
  max-height: 54px;
  object-fit: contain;
  filter: drop-shadow(0 5px 5px rgba(0, 0, 0, 0.18));
}

.icon {
  font-size: 36px;
  margin-bottom: 6px;
  color: #2A8C8C;
  filter: drop-shadow(0 4px 4px rgba(0, 0, 0, 0.18));
}

.node-text {
  text-align: center;
}

.node-title {
  font-weight: 700;
  font-size: 12px;
  margin-bottom: 3px;
  color: #1A5C5C;
}

.content {
  font-size: 11px;
  line-height: 1.25;
  color: #174f4f;
}

/* Sin borde */
.no-border {
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
}

.no-border::before,
.no-border::after {
  display: none !important;
  content: none !important;
}

:deep(.vue-flow__handle) {
  width: 10px !important;
  height: 10px !important;
  opacity: 0 !important;
  border: none !important;
  background: transparent !important;
  pointer-events: none !important;
}

/* ========================= */
/* CELDAS DEL DIAGRAMA       */
/* ========================= */

.template-cell {
  width: 96px !important;
  min-width: 96px !important;
  height: 86px !important;
  min-height: 86px !important;
  padding: 6px !important;
  border-radius: 14px !important;
  border: 2px solid rgba(255, 255, 255, 0.95) !important;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.55),
    0 3px 8px rgba(0, 0, 0, 0.16) !important;
  transform: none !important;
  overflow: visible !important;
  display: flex !important;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: default;
}

.template-cell::before,
.template-cell::after {
  display: none !important;
  content: none !important;
}

.template-cell:hover {
  transform: translateY(-3px) scale(1.04) !important;
  z-index: 10;
  box-shadow:
    0 8px 16px rgba(0, 0, 0, 0.20),
    0 0 14px rgba(255, 255, 255, 0.50) !important;
}

.template-cell .node-image-wrapper {
  width: 100% !important;
  height: 50px !important;
  min-width: auto !important;
  min-height: auto !important;
  margin: 0 0 4px 0 !important;
  padding: 0 !important;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  transform: none !important;
}

.template-cell .node-image {
  max-width: 55px !important;
  max-height: 48px !important;
  object-fit: contain !important;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  filter: drop-shadow(0 3px 3px rgba(0, 0, 0, 0.16));
}

.template-cell .node-text {
  transform: none !important;
  text-align: center;
  margin: 0 !important;
  background: transparent !important;
}

.template-cell .node-title {
  font-family: "Orbitron", sans-serif !important;
  font-size: 8.5px !important;
  line-height: 1.05;
  font-weight: 800;
  color: #244946;
  margin: 0 !important;
  text-shadow: none;
}

.template-cell .content {
  display: block !important;
  margin-top: 3px;
  font-size: 10px !important;
  line-height: 1.1;
  font-weight: 700;
  color: #31504c;
}

/* Colores por bloque */
.template-cell.cell-electricidad {
  background: linear-gradient(135deg, #faefd4, #ebe3d0) !important;
}

.template-cell.cell-hidrogeno {
  background: linear-gradient(135deg, #e9eee7, #b1c2aa) !important;
 box-shadow:
    0 0 0 2px rgba(159, 248, 137, 0.35),
    0 0 10px rgba(126, 255, 132, 0.85),
    0 0 12px rgba(126, 255, 166, 0.65),
    0 0 30px rgba(126, 255, 166, 0.38),
    0 10px 16px rgba(36, 73, 70, 0.30),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -8px 12px rgba(0, 0, 0, 0.10) !important;




}

.template-cell.cell-demanda {
  background: linear-gradient(135deg, #f0e3d5, #f0d7be) !important;
  box-shadow:
    0 0 0 2px rgba(248, 246, 137, 0.35),
    0 0 10px rgba(246, 255, 126, 0.85),
    0 0 12px rgba(242, 255, 126, 0.65),
    0 0 30px rgba(255, 253, 126, 0.38),
    0 10px 16px rgba(36, 73, 70, 0.30),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -8px 12px rgba(0, 0, 0, 0.10) !important;

}
.template-cell.cell-electricidad-top {
  position: relative !important;
  overflow: visible !important;

  background:
    linear-gradient(145deg, #f8f3e8 0%, #d9d0bd 100%) !important;

  border: 2px solid rgba(255, 255, 255, 0.95) !important;

  box-shadow:
    0 0 0 2px rgba(114, 255, 221, 0.35),
    0 0 10px rgba(114, 255, 221, 0.85),
    0 0 22px rgba(114, 255, 221, 0.65),
    0 0 42px rgba(114, 255, 221, 0.38),
    0 10px 16px rgba(36, 73, 70, 0.30),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -8px 12px rgba(0, 0, 0, 0.10) !important;
}
.template-cell.cell-electricidad-top.halo-green {
  box-shadow:
    0 0 0 2px rgba(126, 255, 166, 0.35),
    0 0 10px rgba(126, 255, 166, 0.85),
    0 0 12px rgba(126, 255, 166, 0.65),
    0 0 30px rgba(126, 255, 166, 0.38),
    0 10px 16px rgba(36, 73, 70, 0.30),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -8px 12px rgba(0, 0, 0, 0.10) !important;
}

.template-cell.cell-electricidad-top.halo-blue {
  box-shadow:
    0 0 0 2px rgba(96, 210, 255, 0.35),
    0 0 10px rgba(96, 210, 255, 0.85),
    0 0 12px rgba(96, 210, 255, 0.65),
    0 0 30px rgba(96, 210, 255, 0.38),
    0 10px 16px rgba(36, 73, 70, 0.30),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -8px 12px rgba(0, 0, 0, 0.10) !important;
}

/* Celda vacía */
.template-cell-empty,
.template-cell.cell-empty {
  border: 1px dashed rgba(255, 255, 255, 0.45) !important;
  background:
    repeating-linear-gradient(
      45deg,
      rgba(255, 255, 255, 0.22),
      rgba(255, 255, 255, 0.22) 6px,
      rgba(255, 255, 255, 0.08) 6px,
      rgba(255, 255, 255, 0.08) 12px
    ) !important;
  box-shadow: inset 0 0 8px rgba(0, 0, 0, 0.10) !important;
}

.empty-cell-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.45);
}

/* ========================= */
/* CELDAS CON RESULTADOS     */
/* ========================= */

.template-cell.cell-results {
  width: 150px !important;
  min-width: 150px !important;
  height: 150px !important;
  min-height: 150px !important;
  padding: 10px !important;
}

.template-cell.cell-results .node-image-wrapper {
  height: 72px !important;
  margin-bottom: 6px !important;
}

.template-cell.cell-results .node-image {
  max-width: 88px !important;
  max-height: 68px !important;
}

.template-cell.cell-results .node-title {
  font-size: 11px !important;
  line-height: 1.12 !important;
}

.template-cell.cell-results .content {
  font-size: 12px !important;
  line-height: 1.18 !important;
  margin-top: 4px !important;
}

/* ========================= */
/* TARJETA RESULTADOS        */
/* ========================= */

.results-card-node {
  width: 430px !important;
  min-width: 430px !important;
  min-height: 320px !important;
  padding: 18px !important;
  border-radius: 18px !important;
  border: 2px solid rgba(49, 99, 95, 0.22) !important;
  background: linear-gradient(135deg, #ffffff, #eef7f6) !important;
  box-shadow:
    0 8px 18px rgba(49, 99, 95, 0.16),
    inset 0 1px 0 rgba(255,255,255,0.85) !important;
  transform: none !important;
}

.results-card-node::before,
.results-card-node::after {
  display: none !important;
  content: none !important;
}

.results-card-node .node-text {
  width: 100%;
  text-align: left !important;
}

.results-card-node .content {
  width: 100%;
  display: block !important;
  font-size: 12px !important;
  color: #244946;
}

/* 
  Importante:
  Todo lo que viene dentro de v-html necesita :deep()
*/
.results-card-node :deep(.results-table-wrapper) {
  width: 100%;
}

.results-card-node :deep(.results-table-title) {
  font-family: "Orbitron", sans-serif !important;
  font-size: 18px;
  font-weight: 900;
  color: #244946;
  margin-bottom: 16px;
  text-align: left;
}

.results-card-node :deep(.results-card-table) {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: #ffffff;
  border-radius: 10px;
  overflow: hidden;
}

.results-card-node :deep(.results-card-table thead tr) {
  background: #31635f;
}

.results-card-node :deep(.results-card-table th) {
  padding: 11px 12px;
  color: #ffffff;
  text-align: left;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
}

.results-card-node :deep(.results-card-table th:last-child) {
  text-align: right;
}

.results-card-node :deep(.results-card-table td) {
  padding: 11px 12px;
  border-bottom: 1px solid rgba(49, 99, 95, 0.14);
  color: #244946;
  font-size: 12px;
}

.results-card-node :deep(.results-card-table td:last-child) {
  text-align: right;
  font-weight: 900;
}

.results-card-node :deep(.results-card-table tbody tr:nth-child(even) td) {
  background: rgba(49, 99, 95, 0.06);
}

.results-card-node :deep(.results-card-table tbody tr:last-child td) {
  border-bottom: none;
}
/* ========================= */
/* FLECHAS DEL DIAGRAMA      */
/* ========================= */

.arrow-node {
  min-width: 0 !important;
  min-height: 0 !important;
  width: 100% !important;
  height: 100% !important;
  padding: 0 !important;
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
  pointer-events: none !important;
  overflow: visible !important;
}

.arrow-node .node-text,
.arrow-node .content {
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  display: block !important;
  overflow: visible !important;
}

.arrow-node :deep(.diagram-flow-arrow) {
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.arrow-node :deep(.diagram-flow-arrow-svg) {
  width: 100%;
  height: 100%;
  display: block;
  overflow: visible;
}

.arrow-node :deep(.diagram-flow-arrow-glow) {
  fill: none;
  stroke: rgba(90, 205, 190, 0.42);
  stroke-width: 12;
  stroke-linecap: round;
  stroke-linejoin: round;
  filter: drop-shadow(0 0 8px rgba(90, 205, 190, 0.65));
}

.arrow-node :deep(.diagram-flow-arrow-line) {
  fill: none;
  stroke: #72d6c9;
  stroke-width: 5;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.arrow-node :deep(.diagram-flow-arrow-head) {
  fill: #72d6c9;
  filter: drop-shadow(0 0 6px rgba(90, 205, 190, 0.65));
}
</style>